import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CourseInfoForm } from '../../../components/CourseInfoForm/CourseInfoForm';
import { UpdateImages } from './UpdateImages/UpdateImages';
import { Button } from '../../../components/Button/Button';
import { UpdateVideo, VideoItem } from './UpdateVideo/UpdateVideo';
import { ProductUpdatePreview } from './ProductUpdatePreview/ProductUpdatePreview';
import { ReactComponent as BackButtonIcon } from '../../../assets/icons/back-button-icon.svg';
import { ReactComponent as CloseBackButton } from '../../../assets/icons/close-back-button.svg';
import { fetchAdminProductById, uploadImage, deleteImage, updateProduct } from '../../../api/productApi';
import styles from './UpdateProductPage.module.scss';

export type VideoItemEx = VideoItem & {
    id?: string;
    url?: string;
    thumbnail?: string;
};

type Step = 1 | 2 | 3;

type ImageItem = { id: string; url: string } | null;

export function UpdateProductPage() {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [activeStep, setActiveStep] = useState<Step>(1);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const [content, setContent] = useState<{
        description: string;
        videos: VideoItemEx[];
    }>({
        description: '',
        videos: [{ file: null, previewUrl: null, title: "", isDemo: false }],
    });

    const [courseData, setCourseData] = useState({
        title: '',
        author: '',
        price: '',
        discount: '',
    });

    const [images, setImages] = useState<ImageItem[]>(Array(9).fill(null));
    const [uploading, setUploading] = useState<boolean[]>(Array(9).fill(false));

    const loadProduct = async () => {
        if (!id) return;
        try {
            setLoading(true);
            setError(null);
            const data = await fetchAdminProductById(id);
            const p: any = (data as any).product ?? data;

            setCourseData({
                title: p.title ?? '',
                author: p.author ?? '',
                price: p.price != null ? String(p.price) : '',
                discount: p.discount != null ? String(p.discount) : '',
            });

            setContent((prev) => ({
                ...prev,
                description: p.description ?? '',
                videos: Array.isArray(p.videos) && p.videos.length > 0
                    ? p.videos.map((v: any) => ({
                        file: null,
                        title: v.title ?? '',
                        isDemo: Boolean(v.demo),
                        id: v.id,
                        url: v.url,
                        thumbnail: v.thumbnail,
                    }))
                    : [{ file: null, title: '', isDemo: false }],
            }));

            const slots: ImageItem[] = Array(9).fill(null);
            if (Array.isArray(p.images)) {
                p.images.slice(0, 9).forEach((img: any, idx: number) => {
                    if (img?.id && img?.url) {
                        slots[idx] = { id: img.id, url: img.url };
                    }
                });
            }
            setImages(slots);
        } catch (e: any) {
            setError(e?.message || 'Не удалось загрузить продукт');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveAndRefresh = async () => {
        setLoading(true);
        try {
            await handleSave({ noRedirect: true });
            await loadProduct();
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadProduct();
    }, [id]);

    function normalizeImages(arr: (ImageItem | null)[]): (ImageItem | null)[] {
        const filtered = arr.filter((img): img is ImageItem => img !== null);
        return [...filtered, ...Array(9 - filtered.length).fill(null)];
    }

    const handleImageChange = async (file: File, index: number) => {
        try {
            setUploading((prev) => {
                const copy = [...prev];
                copy[index] = true;
                return copy;
            });

            const formData = new FormData();
            formData.append("productId", id!);
            formData.append(`images[${index}]`, file);

            const updatedImages = await uploadImage(formData);
            const newImage = updatedImages[0];

            if (newImage) {
                setImages((prev) => {
                    const copy = [...prev];
                    copy[index] = newImage;
                    return normalizeImages(copy);
                });
            }
        } catch (e) {
            console.error("Ошибка загрузки изображения", e);
        } finally {
            setUploading((prev) => {
                const copy = [...prev];
                copy[index] = false;
                return copy;
            });
        }
    };

    const handleRemove = async (index: number) => {
        const img = images[index];
        if (img && img.id) {
            try {
                await deleteImage(img.id);
            } catch (e) {
                console.error("Ошибка удаления изображения", e);
            }
        }

        setImages((prev) => {
            const copy = [...prev];
            copy[index] = null;
            return normalizeImages(copy);
        });
    };

    const goBack = () => navigate('/admin/dashboard/', { replace: true });

    const handleCourseChange = (field: string, value: string) => {
        setCourseData((prev) => ({ ...prev, [field]: value }));
    };

    const handleSave = async (options?: { noRedirect?: boolean }) => {
        setLoading(true);
        try {
            await updateProduct({
                id: id!,
                title: courseData.title,
                author: courseData.author,
                price: Number(courseData.price),
                discount: Number(courseData.discount),
                description: content.description
            });
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
            if (!options?.noRedirect) {
                navigate('/admin/dashboard/');
            }
        }
    };

    const isProductValid = useMemo(() => {
        const hasImages = images.some(Boolean);
        const allVideosHaveTitle = content.videos.every((v) => v.title.trim() !== '');
        return (
            courseData.title.trim() !== '' &&
            courseData.author.trim() !== '' &&
            courseData.price.trim() !== '' &&
            hasImages &&
            content.description.trim() !== '' &&
            allVideosHaveTitle
        );
    }, [courseData, images, content]);

    if (loading) {
        return (
            <div className={styles.page}>
                <div className={styles.titleRow}>
                    <h1 className={styles.pageTitle}>Загрузка товара…</h1>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.page}>
                <div className={styles.titleRow}>
                    <h1 className={styles.pageTitle}>Ошибка</h1>
                </div>
                <p className={styles.error}>{error}</p>
                <div className={styles.buttonsRow}>
                    <Button label="Назад" variant="cancel" onClick={goBack} />
                </div>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <div className={styles.backRow}>
                <button className={styles.backBtn} onClick={goBack} aria-label="Назад">
                    <BackButtonIcon />
                </button>
            </div>
            <div className={styles.titleRow}>
                <h1 className={styles.pageTitle}>Обновление товара</h1>
                <button className={styles.closeBtn} onClick={goBack} aria-label="Закрыть и вернуться">
                    <CloseBackButton />
                </button>
            </div>
            <div className={styles.stepsWrapper}>
                <div className={styles.topBar}>
                    {[1, 2, 3].map((step) => (
                        <button key={step} type="button" className={`${styles.stepCard} ${styles[`step${step}`]} ${activeStep === step ? styles.active : ''}`} onClick={() => setActiveStep(step as Step)}>
                            <span className={`${styles.phaseCircle} ${activeStep === step ? styles.phaseActive : ''}`}>{step}</span>
                            <span className={styles.stepLabel}>
                                {step === 1 ? 'Информация о товаре' : step === 2 ? 'Содержание курса' : 'Предварительный просмотр'}
                            </span>
                        </button>
                    ))}
                </div>
            </div>
            <div className={styles.headerLine}></div>
            <div className={styles.contentArea}>
                {activeStep === 1 && (
                    <>
                        <CourseInfoForm data={courseData} onChange={handleCourseChange} />
                        <UpdateImages images={images} onChange={handleImageChange} onRemove={handleRemove} uploading={uploading} />
                        <div className={styles.headerLine}></div>
                        <div className={styles.buttonsRow}>
                            <Button label="Отменить" variant="cancel" onClick={() => navigate('/admin/dashboard')} />
                            <Button label="К следующему шагу" variant="next" onClick={() => setActiveStep(2 as Step)} />
                        </div>
                    </>
                )}
                {activeStep === 2 && (
                    <>
                        <UpdateVideo
                            productId={id!}
                            description={content.description}
                            videos={content.videos}
                            onDescriptionChange={(val) => setContent((p) => ({ ...p, description: val }))}
                            onVideosChange={(items) => setContent((p) => ({ ...p, videos: items as VideoItemEx[] }))}
                            onRefreshData={handleSaveAndRefresh}
                        />
                        <div className={styles.headerLine}></div>
                        <div className={styles.buttonsRow}>
                            <Button label="Назад" variant="cancel" onClick={() => setActiveStep(1 as Step)} />
                            <Button label="К следующему шагу" variant="next" onClick={() => setActiveStep(3 as Step)} />
                        </div>
                    </>
                )}
                {activeStep === 3 && (
                    <div className={styles.card}>
                        <ProductUpdatePreview
                            title={courseData.title}
                            author={courseData.author}
                            price={courseData.price}
                            discount={courseData.discount}
                            images={images.map((img) => (img ? img.url : null))}
                            description={content.description}
                            videos={content.videos.map((v) => ({
                                title: v.title,
                                isDemo: v.isDemo,
                                fileName: v.file ? v.file.name : null,
                                url: v.url,
                            }))}
                        />
                        <div className={styles.headerLine}></div>
                        <div className={styles.buttonsRow}>
                            <Button label="Назад" variant="cancel" onClick={() => setActiveStep(2 as Step)} />
                            <Button label="Сохранить продукт" variant="next" onClick={handleSave} disabled={!isProductValid} />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
